import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import useToast from '@/components/Toast/useToast';
import { Button, Input, Tooltip } from '@/design-system';

import { useValidateForm } from '../../hooks/useValidateForm';
import { InsightsJobFormData } from '../../types';
import { convertFormDataToConfig } from '../../utils/formatUtils';
import { useCreateInsightsJobConfig } from './hooks/useInsightsJobConfigs';

export const SaveAsDialog = ({
  isOpen,
  onOpenChange,
  formData,
  setErrors,
  setSelectedConfig,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: InsightsJobFormData;
  setErrors: (
    errors: Partial<Record<keyof InsightsJobFormData, string>>
  ) => void;
  setSelectedConfig: (id: string) => void;
}) => {
  const { createToast } = useToast();
  const { validateForm } = useValidateForm({
    formData,
    setErrors,
  });
  const { sessionId } = useParams();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const nameError =
    name.trim().length === 0 ? 'Name cannot be empty' : undefined;

  const onClose = () => {
    setName('');
    onOpenChange(false);
  };

  const {
    trigger: createInsightsJobConfig,
    isMutating: isCreatingInsightsJobConfig,
  } = useCreateInsightsJobConfig({
    sessionId: sessionId ?? '',
    options: {
      onError: (e) => {
        createToast({
          title: 'Error creating config',
          description: e.message,
          type: 'error',
        });
      },
      onSuccess: (data) => {
        setSelectedConfig(data.id);
        onClose();
        createToast({
          title: 'Config created',
          description: 'Config created successfully',
          type: 'success',
        });
      },
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent title="New Configuration">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!validateForm()) {
              createToast({
                title: 'Config is invalid',
                description: 'Please fix the errors before saving.',
                type: 'error',
              });
              onClose();
            }
            await createInsightsJobConfig({
              json: {
                name,
                description,
                config: convertFormDataToConfig(formData),
              },
            });
          }}
          className="flex flex-col gap-3"
        >
          <Input
            label="Configuration name"
            placeholder="Name"
            onChange={(value) => setName(value)}
            value={name}
          />
          <Input
            label="Description"
            placeholder="Description"
            onChange={(value) => setDescription(value)}
            value={description}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outlined"
              color="secondary"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Tooltip title={nameError}>
              <Button
                color="primary"
                size="sm"
                type="submit"
                loading={isCreatingInsightsJobConfig}
                disabled={!!nameError}
              >
                Create
              </Button>
            </Tooltip>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
